/**
 * Triggers card on the daemon page. Template example: NextAdmin `tables/basic-tables/_component/leads-report`
 * (card + table with a row ⋯ menu) and demo `/tables/data-tables`; the add dialog follows demo `/ui-elements/dialogs`.
 */
import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useController, useWatch } from 'react-hook-form';
import { Clock, Hand, Link, Pencil, Plus, Trash2, Zap } from 'lucide-react';
import { listWorkflowsOptions } from '../../../generated/api/@tanstack/react-query.gen.js';
import { zDaemonTriggerCreate, zDaemonTriggerUpdate } from '../../../generated/api/zod.gen.js';
import {
  Form,
  FormActions,
  NumberFormField,
  SelectFormField,
  TextFormField,
  useZodForm,
} from '../../../components/form/index.js';
import {
  ActionMenu,
  Badge,
  Button,
  Card,
  ConfirmDialog,
  DataTable,
  type DataTableColumn,
  EmptyState,
  Modal,
  QueryBoundary,
  ScheduleDisplay,
  ScheduleField,
  Toggle,
  useToast,
} from '../../../components/ui/index.js';
import { Can } from '../../../components/auth/Can.js';
import { useDaemonScope } from './DaemonScopeContext.js';
import { daemonKeys, daemonReads, daemonWrites } from './daemon-queries.js';
import type { DaemonTriggerData } from '../../../types/daemon.js';
import type { DaemonRecord } from '../../../generated/api/types.gen.js';

interface DaemonTriggerListProps {
  daemon: DaemonRecord;
}

const TRIGGER_ICONS: Record<string, React.ReactNode> = {
  cron: <Clock size={14} className="text-accent-text" />,
  event: <Zap size={14} className="text-warning" />,
  chained: <Link size={14} className="text-success" />,
  manual: <Hand size={14} className="text-muted" />,
};

const TRIGGER_VARIANTS: Record<string, 'info' | 'warning' | 'success' | 'neutral'> = {
  cron: 'info',
  event: 'warning',
  chained: 'success',
  manual: 'neutral',
};

const TRIGGER_LABELS: Record<string, string> = {
  cron: 'Cron',
  event: 'Event',
  chained: 'Chained',
  manual: 'Manual',
};

const TYPE_OPTIONS = [
  { value: 'cron', label: 'Cron schedule' },
  { value: 'event', label: 'Event pattern' },
  { value: 'chained', label: 'Chained (after another daemon)' },
  { value: 'manual', label: 'Manual' },
];

/** The generated create schema, narrowed to what the dialog edits, plus the per-type required field. */
const addSchema = zDaemonTriggerCreate
  .pick({ type: true, cron: true, eventPattern: true, chainDaemonId: true, workflowDefinitionId: true })
  .superRefine((value, ctx) => {
    if (value.type === 'cron' && !value.cron) ctx.addIssue({ code: 'custom', path: ['cron'], message: 'Required' });
    if (value.type === 'event' && !value.eventPattern)
      ctx.addIssue({ code: 'custom', path: ['eventPattern'], message: 'Required' });
    if (value.type === 'chained' && value.chainDaemonId === undefined)
      ctx.addIssue({ code: 'custom', path: ['chainDaemonId'], message: 'Required' });
  });

function CronFormField() {
  const { field } = useController({ name: 'cron' });
  return <ScheduleField label="Cron expression" value={field.value ?? ''} onChange={field.onChange} />;
}

interface AddTriggerDialogProps {
  open: boolean;
  onClose: () => void;
  daemonId: number;
}

function AddTriggerDialog({ open, onClose, daemonId }: AddTriggerDialogProps) {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const { scope } = useDaemonScope();
  const create = useMutation(daemonWrites(scope).createTrigger());
  const workflows = useQuery({ ...listWorkflowsOptions(), staleTime: 60_000 });
  const form = useZodForm(addSchema, {
    defaultValues: {
      type: 'cron',
      cron: '0 * * * *',
      eventPattern: '',
      chainDaemonId: undefined,
      workflowDefinitionId: undefined,
    },
  });
  const type = useWatch({ control: form.control, name: 'type' });

  const workflowOptions = (workflows.data?.data ?? []).map((w) => ({
    value: w.id,
    label: `${w.name} (${w.stepCount ?? 0} steps)`,
  }));

  const close = () => {
    form.reset();
    onClose();
  };

  const submit = async (values: { type: 'cron' | 'event' | 'manual' | 'chained' } & Record<string, unknown>) => {
    await create.mutateAsync({
      path: { id: String(daemonId) },
      body: {
        type: values.type,
        enabled: true,
        ...(values.type === 'cron' && { cron: String(values.cron) }),
        ...(values.type === 'event' && { eventPattern: String(values.eventPattern) }),
        ...(values.type === 'chained' && { chainDaemonId: Number(values.chainDaemonId) }),
        ...(typeof values.workflowDefinitionId === 'number' && { workflowDefinitionId: values.workflowDefinitionId }),
      },
    });
    await queryClient.invalidateQueries({ queryKey: daemonKeys(scope).triggers(daemonId) });
    notify('Trigger added');
    close();
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title="Add trigger"
      titleIcon={<Plus size={16} />}
      dismissable={!create.isPending}
    >
      <Form form={form} onSubmit={submit} className="space-y-4">
        <SelectFormField name="type" label="Type" options={TYPE_OPTIONS} required />
        {type === 'cron' && <CronFormField />}
        {type === 'event' && (
          <TextFormField name="eventPattern" label="Event pattern" placeholder="e.g. strategy.signal.generated" />
        )}
        {type === 'chained' && (
          <NumberFormField name="chainDaemonId" label="Chain after daemon ID" placeholder="Daemon ID" />
        )}
        <SelectFormField
          name="workflowDefinitionId"
          label="Workflow (optional)"
          options={workflowOptions}
          placeholder="Default (all assigned workflows)"
        />
        <FormActions submitLabel="Add trigger" onCancel={close} />
      </Form>
    </Modal>
  );
}

const editSchema = zDaemonTriggerUpdate.pick({ workflowDefinitionId: true });

interface EditTriggerDialogProps {
  trigger: DaemonTriggerData | null;
  onClose: () => void;
  daemonId: number;
}

/** Changes which workflow one trigger starts; empty means "all assigned workflows". */
function EditTriggerDialog({ trigger, onClose, daemonId }: EditTriggerDialogProps) {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const { scope } = useDaemonScope();
  const update = useMutation(daemonWrites(scope).updateTrigger());
  const workflows = useQuery({ ...listWorkflowsOptions(), staleTime: 60_000 });
  const form = useZodForm(editSchema, { defaultValues: { workflowDefinitionId: null } });

  useEffect(() => {
    if (trigger) form.reset({ workflowDefinitionId: trigger.workflowDefinitionId ?? null });
  }, [trigger, form]);

  const workflowOptions = (workflows.data?.data ?? []).map((w) => ({
    value: w.id,
    label: `${w.name} (${w.stepCount ?? 0} steps)`,
  }));

  const submit = async (values: { workflowDefinitionId?: number | null }) => {
    if (!trigger) return;
    await update.mutateAsync({
      path: { id: String(daemonId), triggerId: String(trigger.id) },
      body: { workflowDefinitionId: values.workflowDefinitionId ?? null },
    });
    await queryClient.invalidateQueries({ queryKey: daemonKeys(scope).triggers(daemonId) });
    notify('Trigger updated');
    onClose();
  };

  return (
    <Modal
      open={trigger !== null}
      onClose={onClose}
      title="Edit trigger"
      titleIcon={<Pencil size={16} />}
      dismissable={!update.isPending}
    >
      <Form form={form} onSubmit={submit} className="space-y-4">
        <SelectFormField
          name="workflowDefinitionId"
          label="Workflow"
          options={workflowOptions}
          placeholder="Default (all assigned workflows)"
          emptyAs={null}
        />
        <FormActions submitLabel="Save trigger" onCancel={onClose} />
      </Form>
    </Modal>
  );
}

export const DaemonTriggerList: React.FC<DaemonTriggerListProps> = ({ daemon }) => {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const { scope, policy, entityLabel } = useDaemonScope();
  const [showAdd, setShowAdd] = useState(false);
  const [deleting, setDeleting] = useState<DaemonTriggerData | null>(null);
  const [editing, setEditing] = useState<DaemonTriggerData | null>(null);
  const workflows = useQuery({ ...listWorkflowsOptions(), staleTime: 60_000 });
  const workflowNames = new Map((workflows.data?.data ?? []).map((w) => [w.id, w.name]));

  const keys = daemonKeys(scope);
  const query = useQuery(daemonReads(scope).triggers(daemon.id));
  const writes = daemonWrites(scope);
  const refresh = () => queryClient.invalidateQueries({ queryKey: keys.triggers(daemon.id) });

  const toggle = useMutation({
    ...writes.updateTrigger(),
    onSuccess: refresh,
    onError: () => notify('Could not change the trigger', 'danger'),
  });
  const remove = useMutation({
    ...writes.deleteTrigger(),
    onSuccess: async () => {
      notify('Trigger deleted');
      setDeleting(null);
      await refresh();
    },
    onError: () => notify('Could not delete the trigger', 'danger'),
  });

  const columns: DataTableColumn<DaemonTriggerData>[] = [
    {
      key: 'type',
      header: 'Type',
      render: (t) => (
        <span className="flex items-center gap-2.5">
          {TRIGGER_ICONS[t.type ?? 'manual']}
          <Badge variant={TRIGGER_VARIANTS[t.type ?? 'manual']} size="sm">
            {TRIGGER_LABELS[t.type ?? 'manual'] ?? t.type}
          </Badge>
        </span>
      ),
    },
    {
      key: 'detail',
      header: 'When',
      render: (t) => {
        if (t.type === 'cron') return <ScheduleDisplay cron={t.cron ?? ''} />;
        if (t.type === 'event') return <span className="font-mono text-xs text-body">{t.eventPattern}</span>;
        if (t.type === 'chained')
          return (
            <span className="text-xs text-body">
              After {entityLabel} #{t.chainDaemonId}
            </span>
          );
        return <span className="text-xs text-muted">Manual only</span>;
      },
    },
    {
      key: 'workflow',
      header: 'Workflow',
      render: (t) =>
        t.workflowDefinitionId ? (
          <span className="text-xs text-body">
            {workflowNames.get(t.workflowDefinitionId) ?? `Workflow ${t.workflowDefinitionId}`}
          </span>
        ) : (
          <span className="text-xs text-muted">All assigned</span>
        ),
    },
    {
      key: 'enabled',
      header: 'Status',
      render: (t) => (
        <Can
          policy={policy}
          fallback={<Badge variant={t.enabled ? 'success' : 'neutral'}>{t.enabled ? 'On' : 'Off'}</Badge>}
        >
          <Toggle
            size="sm"
            aria-label={`Trigger ${t.id} enabled`}
            checked={Boolean(t.enabled)}
            disabled={toggle.isPending}
            onChange={(enabled) =>
              toggle.mutate({ path: { id: String(daemon.id), triggerId: String(t.id) }, body: { enabled } })
            }
          />
        </Can>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (t) => (
        <Can policy={policy}>
          <ActionMenu
            aria-label={`Actions for trigger ${t.id}`}
            items={[
              { id: 'edit', label: 'Edit workflow', icon: <Pencil size={14} />, onAction: () => setEditing(t) },
              {
                id: 'delete',
                label: 'Delete',
                icon: <Trash2 size={14} />,
                tone: 'danger',
                onAction: () => setDeleting(t),
              },
            ]}
          />
        </Can>
      ),
    },
  ];

  return (
    <Card
      title="Triggers"
      actions={
        <Can policy={policy}>
          <Button size="sm" variant="secondary" onClick={() => setShowAdd(true)}>
            <Plus size={14} /> Add trigger
          </Button>
        </Can>
      }
    >
      <QueryBoundary query={query} label="triggers" compact>
        {(data) => (
          <DataTable
            aria-label="Triggers"
            columns={columns}
            rows={data.data}
            rowKey={(t) => t.id}
            empty={
              <EmptyState
                icon={Clock}
                title="No triggers configured"
                description="Add a cron, event, or manual trigger."
              />
            }
          />
        )}
      </QueryBoundary>

      <AddTriggerDialog open={showAdd} onClose={() => setShowAdd(false)} daemonId={daemon.id} />
      <EditTriggerDialog trigger={editing} onClose={() => setEditing(null)} daemonId={daemon.id} />
      <ConfirmDialog
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting && remove.mutate({ path: { id: String(daemon.id), triggerId: String(deleting.id) } })}
        title="Delete trigger"
        message="This trigger stops starting runs. This cannot be undone."
        confirmLabel="Delete trigger"
        tone="danger"
        loading={remove.isPending}
      />
    </Card>
  );
};
